import { useRouter } from "next/router";

import { RhIcon, RhToast } from "@rhythm-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Drawer } from "vaul";

import { AuthContext } from "@/context/AuthContext";

import { JobType } from "@/types/job";

import api from "@/services/api";
import publicApi from "@/services/publicApi";

import { HOST_URL } from "@/constants";

import { useScreenDetector } from "@/hooks/useScreenDetector";

import googleJobSchemaGenerator from "@/helpers/googleJobSchemaGenerator";

import CustomDrawer from "@/components/CustomDrawer";
import CandidateDetailsOverviewSkeleton from "@/components/skeletons/candidate/CandidateDetailsOverviewSkeleton";
import CandidateDetailsTabsSkeleton from "@/components/skeletons/candidate/CandidateDetailsTabsSkeleton";
import Button from "@/components/utils/Button";
import DrawerDetailsResultNavigation from "@/components/utils/DrawerDetailsResultNavigation";

import { JobExpectation, JobPositionAndSkills } from "../JobCardMobileView";
import CreateJobRevokeAppDialog from "./CreateJobRevokeAppDialog";
import JobDetailsTab from "./DrawerTabs/JobDetailsTab";
import JobOverview from "./JobOverview";

const JobDetailsDrawer = ({
  openJobDrawerId,
  setOpenJobDrawerId,
  jobResultCount,
  jobList,
  loadMoreJobs,
  showJobResultNavBar,
  setShowJobResultNavBar,
  serverFetchedJobDetails,
  isPagePublic,
  setWasDrawerExplicitlyClosed,
}: {
  openJobDrawerId: null | number;
  setOpenJobDrawerId: React.Dispatch<React.SetStateAction<number | null>>;
  jobResultCount: number;
  jobList: JobType[];
  loadMoreJobs: () => void;
  showJobResultNavBar: boolean;
  setShowJobResultNavBar: React.Dispatch<React.SetStateAction<boolean>>;
  isPagePublic: boolean | undefined;
  setWasDrawerExplicitlyClosed: React.Dispatch<React.SetStateAction<boolean>>;
  serverFetchedJobDetails: JobType | undefined;
}) => {
  const [resultNumber, setResultNumber] = useState(0);

  const [navigateToNextJob, setNavigateToNextJob] = useState(false);

  const [
    isRevokeApplicationForJobIdDialogOpen,
    setIsRevokeApplicationForJobIdDialogOpen,
  ] = useState<null | number>(null);

  const router = useRouter();

  const {
    data: privateJobDetail,
    isLoading: isPrivateJobLoading,
    isFetching: isPrivateJobFetching,
  } = useQuery({
    queryKey: ["job", { id: openJobDrawerId }, !!isPagePublic],
    queryFn: () => {
      if (!isPagePublic && typeof openJobDrawerId === "number")
        return api.company.getJobById(openJobDrawerId);
      else return Promise.reject(new Error("dependentents are not valid"));
    },
    select: (data) => {
      return data.data;
    },
    enabled: !isPagePublic && typeof openJobDrawerId === "number",
  });

  const {
    data: publicJobDetail,
    isLoading: isPublicJobLoading,
    isFetching: isPublicJobFetching,
  } = useQuery({
    queryKey: ["job", { id: openJobDrawerId }, !!isPagePublic],
    queryFn: () => {
      if (!!isPagePublic && typeof openJobDrawerId === "number")
        return publicApi.getJobById(openJobDrawerId);
      else return Promise.reject(new Error("dependentents are not valid"));
    },
    select: (data) => {
      return data.data;
    },
    enabled: !!isPagePublic && typeof openJobDrawerId === "number",
  });

  const firstLoad = useRef(true);

  useEffect(() => {
    if (openJobDrawerId === null) firstLoad.current = false;
  }, [openJobDrawerId]);

  const jobDetail = !isPagePublic
    ? (privateJobDetail ?? serverFetchedJobDetails)
    : (publicJobDetail ?? serverFetchedJobDetails);

  const isJobLoading = firstLoad.current
    ? !serverFetchedJobDetails
    : !isPagePublic
      ? isPrivateJobLoading
      : isPublicJobLoading;

  const isJobFetching = firstLoad.current
    ? !serverFetchedJobDetails
    : !isPagePublic
      ? isPrivateJobFetching
      : isPublicJobFetching;

  useEffect(() => {
    if (openJobDrawerId === null) return;
    const jobIdx = jobList.findIndex((job) => job.id === openJobDrawerId);
    setResultNumber(jobIdx + 1);
    router.push(`/jobs?id=${openJobDrawerId}`, undefined, { shallow: true });
  }, [openJobDrawerId]);

  const showNextJobResult = async () => {
    const nextJobIndex = resultNumber;
    if (nextJobIndex === jobResultCount) return;
    const nextJobObj = jobList[nextJobIndex];

    if (!nextJobObj) {
      loadMoreJobs();
      setNavigateToNextJob(true);
    } else {
      const nextJobId = nextJobObj.id;
      if (nextJobId) setOpenJobDrawerId(nextJobId);
      setNavigateToNextJob(false);
    }
  };

  useEffect(() => {
    if (!navigateToNextJob) return;
    const nextJobIndex = resultNumber;

    const nextJobObj = jobList[nextJobIndex];
    const nextJobId = nextJobObj.id;
    if (nextJobId) {
      setOpenJobDrawerId(nextJobId);
    }
    setNavigateToNextJob(false);
  }, [jobList]);

  const showPrevJobResult = () => {
    const prevJobIndex = resultNumber - 2;
    if (prevJobIndex === -1) return;
    const nextJobId = jobList[prevJobIndex].id;
    if (nextJobId) setOpenJobDrawerId(nextJobId);
  };

  const onDrawerClose = () => {
    setOpenJobDrawerId(null);
    setShowJobResultNavBar(true);
    router.push("/jobs", undefined, { shallow: true });
    // Set this to true to prevent reopening
    setWasDrawerExplicitlyClosed(true);
  };

  const { isTablet: isShowMobileView } = useScreenDetector();

  return (
    <>
      <CustomDrawer
        className="z-10 w-[calc(100%-248px)]"
        isOpen={openJobDrawerId !== null && !isShowMobileView}
        onClose={onDrawerClose}
        disableCloseOnOutsideClick={
          isRevokeApplicationForJobIdDialogOpen !== null
        }
      >
        <div className="flex h-full flex-col">
          {/* schema will only come up, if job is active. to expire a job, just make the status in-active */}
          {jobDetail && jobDetail.status === "Active" && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(googleJobSchemaGenerator(jobDetail)),
              }}
              // key="product-jsonld"
            />
          )}
          <div
            onClick={onDrawerClose}
            className={
              "absolute top-tnano flex cursor-pointer items-center justify-center rounded-lg bg-white p-tnano " +
              (openJobDrawerId !== null ? "-left-txl" : "")
            }
          >
            <RhIcon icon="mdi:close"></RhIcon>{" "}
          </div>
          {showJobResultNavBar && (
            <DrawerDetailsResultNavigation
              resultLabel={"jobs"}
              resultCount={jobResultCount}
              resultNumber={resultNumber}
              showNextResult={showNextJobResult}
              showPrevResult={showPrevJobResult}
            />
          )}
          <div className="flex flex-1 items-stretch overflow-auto">
            <JobDetail
              setIsRevokeApplicationForJobIdDialogOpen={
                setIsRevokeApplicationForJobIdDialogOpen
              }
              jobDetail={jobDetail}
              isJobLoading={isJobLoading}
              isJobFetching={isJobFetching}
              isPagePublic={isPagePublic}
            />
          </div>
        </div>
      </CustomDrawer>

      <Drawer.Root open={isShowMobileView && openJobDrawerId !== null}>
        <Drawer.Portal>
          <Drawer.Overlay
            onClick={() => onDrawerClose()}
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-20 flex max-h-[96%] flex-col rounded-t-[10px] bg-white lg:hidden">
            <div className="mx-auto flex w-full max-w-md flex-col overflow-y-scroll rounded-t-[10px] px-tmini py-2">
              <MobileDrawerView
                jobDetail={jobDetail}
                setIsRevokeApplicationForJobIdDialogOpen={
                  setIsRevokeApplicationForJobIdDialogOpen
                }
                isPagePublic={isPagePublic}
                onDrawerClose={onDrawerClose}
              />
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {!isPagePublic && (
        <CreateJobRevokeAppDialog
          isRevokeApplicationForJobIdDialogOpen={
            isRevokeApplicationForJobIdDialogOpen
          }
          setIsRevokeApplicationForJobIdDialogOpen={
            setIsRevokeApplicationForJobIdDialogOpen
          }
        />
      )}
    </>
  );
};

export default JobDetailsDrawer;

const JobDetail = ({
  jobDetail,
  isJobLoading,
  isJobFetching,
  setIsRevokeApplicationForJobIdDialogOpen,
  isPagePublic,
}: {
  jobDetail: JobType | null;
  isJobLoading: boolean;
  isJobFetching: boolean;
  setIsRevokeApplicationForJobIdDialogOpen: React.Dispatch<
    React.SetStateAction<null | number>
  >;
  isPagePublic: boolean | undefined;
}) => {
  if (isJobLoading || isJobFetching)
    return (
      <>
        <div className="flex basis-[423px] flex-col gap-4 overflow-auto px-tlg py-tm2">
          <CandidateDetailsOverviewSkeleton />
        </div>
        <div className="flex flex-1 flex-col overflow-auto px-tsm">
          <CandidateDetailsTabsSkeleton />
        </div>
      </>
    );

  if (!jobDetail) {
    return (
      <div className="flex h-full w-full items-center justify-center text-center text-base text-gray-500">
        <span>Unable to load job details. Please try again later.</span>
      </div>
    );
  }

  return (
    <>
      <JobOverview
        className="basis-[423px] overflow-auto"
        jobDetail={jobDetail}
        setIsRevokeApplicationForJobIdDialogOpen={
          setIsRevokeApplicationForJobIdDialogOpen
        }
        isPagePublic={isPagePublic}
      />
      <div className="my-tnano flex flex-1 flex-col overflow-auto px-tsm">
        <div className="flex-1 overflow-auto">
          <JobDetailsTab jobDetail={jobDetail} />
        </div>
      </div>
    </>
  );
};

const MobileDrawerView = ({
  jobDetail,
  isPagePublic,
  setIsRevokeApplicationForJobIdDialogOpen,
  onDrawerClose,
}: {
  jobDetail: JobType | null;
  isPagePublic: boolean | undefined;
  setIsRevokeApplicationForJobIdDialogOpen: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  onDrawerClose: () => void;
}) => {
  const { auth } = useContext(AuthContext);

  const queryClient = useQueryClient();

  const postCandidateJobApplication = useMutation({
    mutationFn: (data: {
      job_requirement: number | undefined;
      // accepted_by: number;
    }) => api.company.postCandidateJobApplication(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["job", { id: data.data.job_requirement }],
      });
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });

      RhToast.success("Applied to job", {
        position: "top-right",
        className: "top-[56px]",
      });
    },
  });

  const router = useRouter();
  const handleCreateApplication = async () => {
    if (isPagePublic && jobDetail) {
      router.push({
        pathname: "/login",
        query: { redirect: `${HOST_URL}/jobs?id=${jobDetail.id}` },
      });
    } else {
      if (!auth.candidateId) return;

      postCandidateJobApplication.mutate({
        job_requirement: jobDetail?.id,
        // accepted_by: auth.candidateId,
      });
    }
  };

  return (
    <>
      <div className="mx-auto mb-5 h-1.5 w-12 flex-shrink-0 rounded-full bg-zinc-300" />
      <div className="flex space-y-tnano py-tnano">
        <div className="mr-tm2 flex h-[50px] w-[50px] items-center justify-between rounded-md border px-tnano">
          {jobDetail?.company?.logo && (
            <img src={jobDetail.company?.logo} className="w-full" />
          )}
        </div>
        <div className="space-y-tpico">
          <p>{jobDetail?.title}</p>
          {jobDetail?.company?.name && (
            <p className="body-small text-light mb-tsm">
              {jobDetail?.company?.name}
            </p>
          )}
        </div>
      </div>
      {jobDetail && <JobPositionAndSkills job={jobDetail} className="" />}
      {jobDetail && <JobExpectation job={jobDetail} className="px-0" />}

      <div>
        {!jobDetail ||
        ((jobDetail && jobDetail.job_description) ||
          jobDetail.job_description) === "" ? (
          "-"
        ) : (
          <>
            <div className="mb-tpico">
              <p className="text-hint">Roles & Responsibilities</p>
            </div>
            <div
              className="body-small"
              dangerouslySetInnerHTML={{
                __html: jobDetail.job_description,
              }}
            />
          </>
        )}
      </div>
      <div className="mt-tsm"></div>

      {jobDetail?.is_applied ? (
        <Button
          className="mt-tnano"
          layout="outline"
          block
          onClick={() => {
            onDrawerClose();
            setIsRevokeApplicationForJobIdDialogOpen(jobDetail.id);
          }}
        >
          Revoke Application
        </Button>
      ) : (
        <Button
          loading={postCandidateJobApplication.isLoading}
          className="mt-tnano"
          block
          onClick={handleCreateApplication}
        >
          Apply
        </Button>
      )}
      <div className="mb-tnano"></div>
    </>
  );
};
